import { haversineKm, type LatLng } from '../game/geo';
import type { Panorama } from './types';

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly url: string,
  ) {
    super(`HTTP ${status}`);
    this.name = 'HttpError';
  }
  get isAuthError() {
    return this.status === 401 || this.status === 403;
  }
}

/** The request never reached the server (offline, DNS, CORS, blocked by an extension…). */
export class NetworkError extends Error {
  constructor(
    public readonly url: string,
    public readonly cause?: unknown,
  ) {
    super('Network error');
    this.name = 'NetworkError';
  }
}

export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException ? error.name === 'AbortError' : (error as Error)?.name === 'AbortError';
}

export function abortError(): DOMException {
  return new DOMException('Aborted', 'AbortError');
}

async function doFetch(url: string, init: RequestInit): Promise<Response> {
  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (error) {
    if (isAbortError(error)) throw error;
    throw new NetworkError(url, error);
  }
  if (!response.ok) throw new HttpError(response.status, url);
  return response;
}

export async function fetchJson<T = unknown>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await doFetch(url, { ...init, headers: { Accept: 'application/json', ...init.headers } });
  return (await response.json()) as T;
}

export async function fetchArrayBuffer(url: string, init: RequestInit = {}): Promise<ArrayBuffer> {
  const response = await doFetch(url, init);
  return response.arrayBuffer();
}

/** True when `position` is far enough from every already used location. */
export function isFarEnough(position: LatLng, exclude: LatLng[], minDistanceKm: number): boolean {
  return exclude.every((p) => haversineKm(p, position) >= minDistanceKm);
}

export interface BatchSearchOptions {
  signal: AbortSignal;
  /** Number of sequential batches. */
  batches: number;
  /** Attempts run in parallel inside a batch. */
  parallel: number;
  /** One attempt: resolves with a panorama, or `null` when nothing suitable was found. */
  attempt: (signal: AbortSignal, index: number) => Promise<Panorama | null>;
}

export interface BatchSearchResult {
  panorama: Panorama | null;
  /** Errors thrown by attempts (abort errors excluded). */
  errors: unknown[];
  attempts: number;
}

/**
 * Runs attempts in parallel batches and resolves with the first panorama found.
 * Remaining attempts of the batch are aborted as soon as one succeeds.
 */
export async function batchSearch({ signal, batches, parallel, attempt }: BatchSearchOptions): Promise<BatchSearchResult> {
  const errors: unknown[] = [];
  let attempts = 0;
  for (let b = 0; b < batches; b++) {
    if (signal.aborted) throw abortError();
    const controller = new AbortController();
    const forwardAbort = () => controller.abort();
    signal.addEventListener('abort', forwardAbort, { once: true });
    try {
      const found = await new Promise<Panorama | null>((resolve) => {
        let pending = parallel;
        for (let i = 0; i < parallel; i++) {
          const index = b * parallel + i;
          attempts++;
          attempt(controller.signal, index)
            .then((panorama) => {
              if (panorama) resolve(panorama);
            })
            .catch((error) => {
              if (!isAbortError(error)) errors.push(error);
            })
            .finally(() => {
              pending--;
              if (pending === 0) resolve(null);
            });
        }
      });
      if (found) return { panorama: found, errors, attempts };
    } finally {
      controller.abort();
      signal.removeEventListener('abort', forwardAbort);
    }
    // Stop early when the service rejects us: retrying won't help.
    if (errors.some((e) => e instanceof HttpError && e.isAuthError)) break;
  }
  if (signal.aborted) throw abortError();
  return { panorama: null, errors, attempts };
}

/** True when every attempt failed with a network/server error (the service is probably unreachable). */
export function allAttemptsFailed(result: BatchSearchResult): boolean {
  return result.attempts > 0 && result.errors.length >= result.attempts;
}

export function describeError(error: unknown): string {
  if (error instanceof HttpError) {
    if (error.isAuthError) return 'accès refusé (clé ou jeton invalide ?)';
    if (error.status === 429) return 'trop de requêtes, réessayez dans un instant';
    if (error.status >= 500) return `le service est en panne (erreur ${error.status})`;
    return `erreur ${error.status}`;
  }
  if (error instanceof NetworkError) return 'service injoignable (connexion ou blocage réseau)';
  if (error instanceof Error) return error.message;
  return String(error);
}

/** Closest panorama to `target`, or null. */
export function nearestPanorama(target: LatLng, panoramas: Panorama[], excludeId: string): Panorama | null {
  let best: Panorama | null = null;
  let bestDistance = Infinity;
  for (const p of panoramas) {
    if (p.id === excludeId) continue;
    const d = haversineKm(target, p.position);
    if (d < bestDistance) {
      bestDistance = d;
      best = p;
    }
  }
  return best;
}
